// Place lookup. ZIP codes, Canadian postal prefixes and city names are answered instantly from a
// bundled GeoNames table; street addresses go to OpenStreetMap's Nominatim, only on request.
import type { Position } from './types';

export type Place = { label: string, position: Position, kind: 'postal' | 'city' | 'address' };

type PostalCode = { code: string, city: string, state: string, lat: number, lng: number };
type City = { name: string, key: string, state: string, lat: number, lng: number, codes: number };

// Polygons of rings of flat [lng, lat, lng, lat, ...] numbers; the first ring is the outline, the rest are holes.
type Land = number[][][];

type Dataset = { postalCodes: PostalCode[], byCode: Map<string, PostalCode>, cities: City[], grid: Map<string, PostalCode[]>, land: Land };

let dataset: Dataset | undefined;
let loading: Promise<Dataset> | undefined;

const cell = (lat: number, lng: number) => `${Math.floor(lat)},${Math.floor(lng)}`;

export function loadPlaces(): Promise<Dataset> {
    loading ??= Promise.all([
        fetch('geo/postal.tsv').then((response) => response.text()),
        fetch('geo/land.json').then((response) => response.json() as Promise<Land>),
    ]).then(([text, land]) => {
        const postalCodes: PostalCode[] = [];
        const byCode = new Map<string, PostalCode>();
        const grid = new Map<string, PostalCode[]>();
        const cityIndex = new Map<string, City>();
        for (const line of text.split('\n')) {
            const [code, city, state, lat, lng] = line.split('\t');
            if (!lng) {
                continue;
            }
            const postalCode = { code, city, state, lat: Number(lat), lng: Number(lng) };
            postalCodes.push(postalCode);
            byCode.set(code, postalCode);
            const key = cell(postalCode.lat, postalCode.lng);
            grid.get(key)?.push(postalCode) ?? grid.set(key, [postalCode]);
            // A city's position is the average of its postal codes.
            const cityKey = `${city.toLowerCase()}|${state}`;
            const existing = cityIndex.get(cityKey);
            if (existing) {
                existing.lat += postalCode.lat;
                existing.lng += postalCode.lng;
                existing.codes += 1;
            } else {
                cityIndex.set(cityKey, { name: city, key: city.toLowerCase(), state, lat: postalCode.lat, lng: postalCode.lng, codes: 1 });
            }
        }
        const cities = [...cityIndex.values()];
        for (const city of cities) {
            city.lat /= city.codes;
            city.lng /= city.codes;
        }
        // More postal codes roughly means a bigger city, which is the one people usually mean.
        cities.sort((a, b) => b.codes - a.codes);
        dataset = { postalCodes, byCode, cities, grid, land };
        return dataset;
    });
    return loading;
}

const postalPlace = (postalCode: PostalCode): Place => ({
    label: `${postalCode.code} · ${postalCode.city}, ${postalCode.state}`,
    position: { lat: postalCode.lat, lng: postalCode.lng },
    kind: 'postal',
});

// Instant suggestions for what has been typed so far. Empty until the table has loaded.
export function suggestPlaces(query: string, limit = 7): Place[] {
    const text = query.trim();
    if (!dataset || text.length < 2) {
        return [];
    }
    const compact = text.replace(/\s+/g, '').toUpperCase();
    if (/^\d{3,5}$/.test(compact) || /^[A-Z]\d[A-Z]/.test(compact)) {
        const prefix = /^\d/.test(compact) ? compact : compact.slice(0, 3);
        const exact = dataset.byCode.get(prefix);
        if (exact) {
            return [postalPlace(exact)];
        }
        return dataset.postalCodes.filter((postalCode) => postalCode.code.startsWith(prefix)).slice(0, limit).map(postalPlace);
    }
    // "Chicago", "Chicago IL" or "Chicago, IL"
    const match = text.match(/^(.*?)[,\s]+([A-Za-z]{2})$/);
    const candidates = match ? [{ name: match[1], state: match[2].toUpperCase() }, { name: text, state: '' }] : [{ name: text, state: '' }];
    const places: Place[] = [];
    for (const { name, state } of candidates) {
        const key = name.trim().toLowerCase();
        const matching = dataset.cities.filter((city) => city.key.startsWith(key) && (!state || city.state === state));
        // Exact names first; the list is already ordered by size.
        matching.sort((a, b) => Number(b.key === key) - Number(a.key === key));
        for (const city of matching) {
            const label = `${city.name}, ${city.state}`;
            if (places.length < limit && !places.some((place) => place.label === label)) {
                places.push({ label, position: { lat: city.lat, lng: city.lng }, kind: 'city' });
            }
        }
    }
    return places;
}

const EARTH_RADIUS_MILES = 3958.8;

export function distanceMiles(a: Position, b: Position): number {
    const radians = Math.PI / 180;
    const dLat = (b.lat - a.lat) * radians;
    const dLng = (b.lng - a.lng) * radians;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * radians) * Math.cos(b.lat * radians) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_MILES * Math.asin(Math.sqrt(h));
}

export type NearestPlace = { label: string, miles: number };

// The closest known postal code within about two degrees, or undefined when there is none.
export function nearestPlace(position: Position): NearestPlace | undefined {
    if (!dataset) {
        return undefined;
    }
    let best: PostalCode | undefined;
    let bestMiles = Infinity;
    for (let dLat = -2; dLat <= 2; dLat++) {
        for (let dLng = -2; dLng <= 2; dLng++) {
            for (const postalCode of dataset.grid.get(cell(position.lat + dLat, position.lng + dLng)) || []) {
                const miles = distanceMiles(position, postalCode);
                if (miles < bestMiles) {
                    best = postalCode;
                    bestMiles = miles;
                }
            }
        }
    }
    return best && { label: `${best.city}, ${best.state}`, miles: bestMiles };
}

function inRing(position: Position, ring: number[]): boolean {
    let inside = false;
    for (let i = 0, j = ring.length - 2; i < ring.length; j = i, i += 2) {
        const crosses = (ring[i + 1] > position.lat) !== (ring[j + 1] > position.lat)
            && position.lng < (ring[j] - ring[i]) * (position.lat - ring[i + 1]) / (ring[j + 1] - ring[i + 1]) + ring[i];
        if (crosses) {
            inside = !inside;
        }
    }
    return inside;
}

// The outline is coarse (about a mile), so a pin in a harbour town can fall just outside it.
const COAST_TOLERANCE_MILES = 25;

// Whether a pin is somewhere a truck could be: on land in the Americas, or right by a known postal
// code. Anything else is out at sea or on another continent, which is how the office has
// always put aside drivers it is not dispatching for.
export function isOnMap(position: Position, nearest = nearestPlace(position)): boolean {
    if (!dataset) {
        return true;
    }
    if (nearest && nearest.miles <= COAST_TOLERANCE_MILES) {
        return true;
    }
    return dataset.land.some(([outline, ...holes]) => inRing(position, outline) && !holes.some((hole) => inRing(position, hole)));
}

// Street address search. Nominatim's usage policy forbids search-as-you-type, so call this
// only when the user explicitly asks.
export async function searchAddress(query: string): Promise<Place[]> {
    const parameters = new URLSearchParams({ q: query, format: 'jsonv2', limit: '6', countrycodes: 'us,ca,mx' });
    const response = await fetch(`https://nominatim.openstreetmap.org/search?${parameters}`);
    if (!response.ok) {
        throw new Error('Address search is unavailable right now');
    }
    const results: { display_name: string, lat: string, lon: string }[] = await response.json();
    return results.map((result) => ({
        label: result.display_name,
        position: { lat: Number(result.lat), lng: Number(result.lon) },
        kind: 'address',
    }));
}
